import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Icon } from "../components/Icon";
import { DEMO_MODE } from "../lib/config";
import { api } from "../lib/api";
import { DEMO_PATIENT } from "../lib/demoData";
import type { Patient } from "../types";

export function PatientsPage() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: async (): Promise<Patient[]> => {
      if (DEMO_MODE) return [DEMO_PATIENT];
      return await api.get<Patient[]>("/patients");
    },
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data;
    return data.filter((p) =>
      `${p.firstName} ${p.middleName} ${p.lastName} ${p.email} ${p.phone}`
        .toLowerCase()
        .includes(needle),
    );
  }, [data, q]);

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Patients</h1>
          <p className="page__sub">All patients in the practice.</p>
        </div>
        <div className="page__actions">
          <Link to="/patients/new" className="btn btn--solid">
            <Icon.Plus width={14} height={14} /> Add patient
          </Link>
        </div>
      </div>

      <input
        className="form__input"
        placeholder="Search by name, email, phone…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 16, maxWidth: 480 }}
      />

      {isLoading && <div className="empty">Loading…</div>}
      {!isLoading && filtered.length === 0 && <div className="empty">No patients found.</div>}
      {!isLoading && filtered.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>DOB</th>
              <th>Phone</th>
              <th>Employer</th>
              <th>SSN</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.firstName} <span style={{ fontStyle: "italic" }}>{p.middleName}</span>{" "}
                  {p.lastName}
                </td>
                <td className="mono">{p.dob}</td>
                <td className="mono">{p.phone}</td>
                <td>{p.employer}</td>
                <td className="mono">•••-••-{p.ssnLast4}</td>
                <td>
                  <Link to={`/sessions/new?patientId=${p.id}`}>Start session</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
